import React, { FC, RefObject, useCallback, useEffect, useMemo } from "react";
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView as RNScrollView,
} from "react-native";
import { ALIGN, Box, View, Spinner, SPACE, Layout as AuroraLayout } from "@lookiero/aurora";
import { useI18n, useI18nMessage } from "@lookiero/i18n-react";
import "@lookiero/sty-psp-ui";
import { Text } from "@lookiero/sty-psp-ui";
import { ProductPreviewWithDiscount } from "../../../../../domain/projections/ProductPreviewWithDiscount";
import { BackToTopButton } from "../../../components/backToTopButton/BackToTopButton";
import { CategoriesBanner } from "../../../components/categoriesBanner/CategoriesBanner";
import { CategoryTabs } from "../../../components/categoryTabs/CategoryTabs";
import { CrossSellingBanner } from "../../../components/crossSellingBanner/CrossSellingBanner";
import { IconMessage } from "../../../components/iconMessage/IconMessage";
import { ProductPreviewList } from "../../../components/productPreviewList/ProductPreviewList";
import { useExtendedScreen } from "../../../hooks/useExtendedScreen";
import { useCheckIfElementsAreInView } from "../../../hooks/useInView";
import { DOMAIN, I18nMessages } from "../../../i18n/i18n";
import { OS } from "../../../shared/config";
import { OfferingContent } from "../offeringLayout/OfferingContent";
import { getTranslationKeyByCategoryId } from "./Sales.controller";
import { style as salesStyle } from "./Sales.style";

const FEATURED_PRODUCT_FREQUENCY = 5;

interface SalesProps {
  readonly categoryIds: string[];
  readonly isLoading: boolean;
  readonly products: ProductPreviewWithDiscount[] | undefined;
  readonly scrollViewRef: RefObject<RNScrollView | null>;
  readonly selectedCategoryId: string;
  readonly onLayoutProductList: (event: LayoutChangeEvent) => void;
  readonly onPressCategory: (category: string) => void;
  readonly onPressProductPreview: (product: ProductPreviewWithDiscount, index: number) => void;
  readonly onScroll: (coords: { X: number; Y: number }) => void;
  readonly onBack: () => void;
}

const Sales: FC<SalesProps> = ({
  categoryIds,
  isLoading,
  products,
  scrollViewRef,
  selectedCategoryId,
  onLayoutProductList,
  onPressCategory,
  onPressProductPreview,
  onScroll,
  onBack,
}) => {
  const screen = useExtendedScreen();
  const isScreenMobile = !screen.XL;
  const isWebDesktop = Platform.OS === OS.WEB && !isScreenMobile;

  const { formatMessage } = useI18n({ domain: DOMAIN });
  const salesTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.SALES_TITLE,
  });
  const salesSubtitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.SALES_SUBTITLE,
  });
  const footerDescriptionText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.FOOTER_DESCRIPTION,
  });
  const footerTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.FOOTER_TITLE,
  });
  const footerLegalText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.FOOTER_LEGAL_TEXT,
  });

  const style = useMemo(() => salesStyle(), []);

  const checkIfElementsAreInView = useCheckIfElementsAreInView();
  const handleOnScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScroll({ X: event.nativeEvent.contentOffset.x, Y: event.nativeEvent.contentOffset.y });

      checkIfElementsAreInView();
    },
    [onScroll, checkIfElementsAreInView],
  );

  // Hack to fix that address bar issue in mobile browsers
  // https://developer.chrome.com/blog/url-bar-resizing/
  // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/

  const onResize = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
    document.body.setAttribute("style", `height: calc(var(--vh, 1vh) * 100);`);
  };

  useEffect(() => {
    if (Platform.OS === OS.WEB) {
      window.addEventListener("resize", onResize);
    }
    return () => {
      if (Platform.OS === "web") {
        window.removeEventListener("resize", onResize);
      }
    };
  }, []);

  const showLegal = useMemo(
    () =>
      products && products.length > 0
        ? products.some((product) =>
            product.price.discountedPrice ? product.price.discountedPrice.percentage > 0 : false,
          )
        : false,
    [products],
  );

  return (
    <OfferingContent
      scrollRef={scrollViewRef}
      style={{ footer: style.footer }}
      footer={
        screen.S && products?.length ? (
          <BackToTopButton scrollViewRef={scrollViewRef} section="shop_sales" />
        ) : undefined
      }
      onScroll={handleOnScroll}
    >
      {isLoading ? (
        <View style={style.spinnerWrapper}>
          <Spinner />
        </View>
      ) : (
        <View style={style.container}>
          <Box style={[style.titleArea, isScreenMobile ? style.titleAreaSmall : undefined]}>
            <Text
              level={isScreenMobile ? 1 : 3}
              style={[style.title, isScreenMobile ? undefined : style.titleLarge]}
              testID="home-title"
              variant={isScreenMobile ? "body" : "heading"}
            >
              {salesTitleText}
            </Text>
            <Text
              level={isScreenMobile ? 2 : 3}
              style={[style.subtitle, isScreenMobile ? undefined : style.subtitleLarge]}
              variant={isScreenMobile ? "detail" : "body"}
            >
              {salesSubtitleText}
            </Text>
          </Box>
          <View
            style={[
              style.tabsAndProducts,
              isWebDesktop ? style.tabsAndProductsWeb : undefined,
              isScreenMobile ? style.tabsAndProductsVertically : style.tabsAndProductsHorizontally,
            ]}
          >
            <View
              style={[
                !isScreenMobile ? style.tabsColumn : style.tabsRow,
                isWebDesktop ? style.tabsColumnWeb : undefined,
              ]}
            >
              <CategoryTabs
                categoryIds={categoryIds}
                horizontal={isScreenMobile}
                i18nKeyExtractor={(categoryId) => formatMessage(getTranslationKeyByCategoryId(categoryId))}
                selectedCategoryId={selectedCategoryId}
                onPress={onPressCategory}
              />
            </View>
            <Box key={selectedCategoryId} style={!isScreenMobile ? style.desktopProductWrapper : undefined}>
              <ProductPreviewList
                banner={(scaled: boolean) => <CategoriesBanner scaled={scaled} />}
                featuredProductFrequency={FEATURED_PRODUCT_FREQUENCY}
                products={products}
                onItemPress={onPressProductPreview}
                onLayoutProductList={onLayoutProductList}
              />
            </Box>
          </View>
          <AuroraLayout>
            <Box align={ALIGN.CENTER} size={{ L: "2/3" }} style={style.bottomMessage}>
              <IconMessage body={footerDescriptionText} title={footerTitleText} />
              <CategoryTabs
                categoryIds={categoryIds}
                i18nKeyExtractor={(categoryId) => formatMessage(getTranslationKeyByCategoryId(categoryId))}
                selectedCategoryId={selectedCategoryId}
                horizontal
                onPress={onPressCategory}
              />
            </Box>
            <Box
              align={ALIGN.CENTER}
              marginBottom={isScreenMobile ? SPACE.SPACE_8 : SPACE.SPACE_20}
              size={{ L: "1/3" }}
            >
              <CrossSellingBanner style={style.crossSellingBanner} onPress={onBack} />
              {showLegal && (
                <Text level={2} style={style.subtitle}>
                  {footerLegalText}
                </Text>
              )}
            </Box>
          </AuroraLayout>
        </View>
      )}
    </OfferingContent>
  );
};

export { Sales };
